import { useEditMyPasswordMutation } from '@/api/api.slice.tsx';
import {
  Alert,
  Button,
  Paper,
  TextFieldPassword,
  Typography,
  getMessageFromError,
  isPasswordStrongEnough,
  renderPasswordHelperText,
} from '@arsnovasystems/ars-front-lib';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import ErrorIcon from '@mui/icons-material/Error';
import { Box, Link, Stack } from '@mui/material';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

interface IFormData {
  password: string;
  passwordBis: string;
}

/**
 * The resetCredentials component
 *
 * @returns JSX of the component
 */
export const ResetCredentials = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { control, handleSubmit } = useForm<IFormData>({
    mode: 'onChange',
    defaultValues: {
      password: '',
      passwordBis: '',
    },
  });

  const [savePartnerPassword, { isLoading, error }] =
    useEditMyPasswordMutation();

  const submitForm = async (data: IFormData) => {
    const result = await savePartnerPassword({ password: data.password });

    if (result.error === undefined) {
      void navigate('/reset-credentials/success');
    }
  };

  return (
    <Layout>
      <form onSubmit={handleSubmit(submitForm)}>
        <Stack sx={{ gap: 2 }}>
          <Controller
            control={control}
            name={'password'}
            rules={{
              required: true,
              validate: {
                hasAnyError: isPasswordStrongEnough,
              },
            }}
            render={({ field, fieldState }) => {
              return (
                <>
                  <TextFieldPassword
                    {...field}
                    error={fieldState.invalid}
                    label={t('ik.new.password')}
                  />
                  {renderPasswordHelperText({
                    fieldValue: field.value,
                    isDirty: fieldState.isDirty,
                    hasError: fieldState.invalid,
                  })}
                </>
              );
            }}
          />
          <Controller
            control={control}
            name={'passwordBis'}
            rules={{
              required: true,
              validate: {
                isMatchingFirstPassword: (value, formValues) => {
                  if (formValues.password !== value)
                    return t('The two passwords are not identical');
                  return formValues.password === value;
                },
              },
            }}
            render={({ field, fieldState }) => {
              return (
                <>
                  <TextFieldPassword
                    {...field}
                    error={fieldState.invalid}
                    label={t('ik.confirm.your.password')}
                  />
                  <Box
                    sx={{
                      marginTop: '2px',
                      marginLeft: '1px',
                    }}
                  >
                    {fieldState.invalid ?
                      <Typography
                        variant={'subtitle2'}
                        color={'error'}
                      >
                        {fieldState.error?.message}
                      </Typography>
                    : null}
                    {fieldState.isDirty && !fieldState.invalid ?
                      <Typography
                        variant={'subtitle2'}
                        color={'success'}
                      >
                        {t('The two passwords are matching ! 🤝')}
                      </Typography>
                    : null}
                  </Box>
                </>
              );
            }}
          />
          {error ?
            <Alert>{getMessageFromError(error)}</Alert>
          : null}
          <Button
            loading={isLoading}
            variant='contained'
            style={{ maxWidth: '100px' }}
            type={'submit'}
          >
            {t('ik.apply')}
          </Button>
        </Stack>
      </form>
    </Layout>
  );
};

const Layout = ({ children }: { children: React.ReactNode }) => {
  return (
    <Box className={`login`}>
      <Paper elevation={2}>
        <Box
          component={'div'}
          className='login__container'
          sx={{ alignItems: 'center' }}
        >
          {children}
        </Box>
      </Paper>
    </Box>
  );
};

/**
 * The ResetCredentialsSuccess component
 *
 * @returns JSX of the component
 */
export const ResetCredentialsSuccess = () => {
  const { t } = useTranslation();

  return (
    <Layout>
      <>
        <Stack
          direction={'row'}
          sx={{ gap: 2, mt: 2, marginBottom: 2 }}
        >
          <CheckCircleOutlineIcon
            sx={{
              fontSize: '4rem',
            }}
            color={'success'}
          />
          <Typography>
            {t('ik.your.password.has.been.successfully.changed')}
          </Typography>
        </Stack>
        <Link
          sx={{ mt: 2 }}
          href='/login'
        >
          {t('ik.back.to.login')}
        </Link>
      </>
    </Layout>
  );
};

/**
 * The reset credentials fails component
 *
 * @returns JSX of the component
 */
export const ResetCredentialsFail = () => {
  const { t } = useTranslation();
  return (
    <Layout>
      <>
        <Stack
          direction={'row'}
          sx={{ gap: 2, mt: 2, marginBottom: 2 }}
        >
          <ErrorIcon
            sx={{
              fontSize: '4rem',
              color: '#ff3b30',
            }}
          />
          <Typography>
            {t('ik.it.appear.that.your.token.is.invalid')}
          </Typography>
        </Stack>
        <Link
          sx={{ mt: 2 }}
          href='/login'
        >
          {t('ik.back.to.login')}
        </Link>
      </>
    </Layout>
  );
};
